import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, HostListener, ElementRef, ViewChild } from '@angular/core';
import { Invoice } from 'src/app/shared/models/invoice.interface';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { GeneralService } from 'src/app/services/general/general.service';

declare var bootstrap: any;

@Component({
  selector: 'app-view-invoice',
  templateUrl: './view-invoice.component.html',
  styleUrls: ['./view-invoice.component.scss']
})
export class ViewInvoiceComponent extends DestructorComponent implements OnInit, OnDestroy, OnChanges {
  @Input() invoices: Invoice[] = [];
  @Input() selectedFormats: string[] = [];
  @ViewChild('modalFormatDropdownContainer') modalFormatDropdownContainer!: ElementRef;

  isGenerating: boolean = false;
  selectedViewMode: string = 'merged'; // Default to merged mode
  pdfUrl: string = '';
  processedInvoices: Invoice[] = []; // Cache processed invoices
  editableData: any = null; // Store editable data from dummy-invoice component
  commercialInvoiceData: any = null; // Store editable data from commercial-invoice component
  activeFormatTab: string = ''; // Currently active format tab
  selectedFormatsInModal: string[] = []; // Formats selected in modal
  showFormatDropdown: boolean = false;
  activeInvoiceIndex: number = 0; // Track active invoice for export certificate tabs

  // Progress tracking for PDF generation
  currentGeneratingIndex: number = 0;
  totalToGenerate: number = 0;

  // Export type ('pdf' | 'excel' | 'both') chosen per format.
  // Only the formats listed in exportCapableFormats expose this choice in the UI.
  exportCapableFormats = ['plain_invoice', 'caricom_invoice', 'commercial_invoice', 'insurance_invoice', 'standard_invoice', 'certificate_of_origin'];
  exportTypeByFormat: { [format: string]: 'pdf' | 'excel' | 'both' } = {};

  // Available PDF formats
  pdfFormats = [
    { value: 'plain_invoice', label: 'Imperial Solutions Format' },
    { value: 'caricom_invoice', label: 'Caricom Invoice Format' },
    { value: 'commercial_invoice', label: 'Commercial Invoice Format' },
    { value: 'standard_invoice', label: 'Dummy Invoice Format' },
    { value: 'shipping_information', label: 'Shipping Information Format' },
    { value: 'export_certificate', label: 'Export Certificate Format' },
    { value: 'house_bill_landing', label: 'House Bill of Lading Format' },
    { value: 'ctn_invoice', label: 'CTN Invoice' },
    { value: 'ctn_invoice_excel', label: 'CTN Invoice Excel' },
    { value: 'certificate_of_origin', label: 'Certificate of Origin' },
    { value: 'insurance_invoice', label: 'Insurance Invoice' }
  ];

  // Available view modes
  viewModes = [
    { value: 'individual', label: 'Individual' },
    { value: 'merged', label: 'Merged' }
  ];

  constructor(private _generalService: GeneralService) {
    super();
  }

  ngOnInit(): void {
    this.updateProcessedInvoices();
    this.initializeFormats();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['invoices']) {
      this.updateProcessedInvoices();
    }
    if (changes['selectedFormats']) {
      this.initializeFormats();
    }
  }

  private initializeFormats(): void {
    // Initialize selected formats from input
    this.selectedFormatsInModal = [...this.selectedFormats];
    // Set active tab to first selected format
    if (this.selectedFormatsInModal.length > 0) {
      this.activeFormatTab = this.selectedFormatsInModal[0];
    }
    // Default every export-capable format to PDF output
    this.exportCapableFormats.forEach(format => {
      if (!this.exportTypeByFormat[format]) {
        this.exportTypeByFormat[format] = 'pdf';
      }
    });
  }

  // --- Export type (PDF / Excel / Both) selection -------------------------

  /** Whether the given format lets the user choose PDF/Excel/Both. */
  supportsExportType(format: string): boolean {
    return this.exportCapableFormats.includes(format);
  }

  getExportType(format: string): 'pdf' | 'excel' | 'both' {
    return this.exportTypeByFormat[format] || 'pdf';
  }

  setExportType(format: string, type: 'pdf' | 'excel' | 'both'): void {
    this.exportTypeByFormat[format] = type;
  }

  /**
   * Resolve which output file(s) a format should produce.
   * - shipping_information & ctn_invoice_excel are always Excel-only.
   * - export-capable formats follow the user's per-format choice.
   * - everything else stays PDF-only.
   */
  private getOutputModes(format: string): ('pdf' | 'excel')[] {
    if (format === 'shipping_information' || format === 'ctn_invoice_excel') {
      return ['excel'];
    }
    if (this.supportsExportType(format)) {
      const type = this.getExportType(format);
      return type === 'both' ? ['pdf', 'excel'] : [type];
    }
    return ['pdf'];
  }

  /** Total number of files to be generated across all selected formats. */
  private computeTotalFiles(): number {
    return this.selectedFormatsInModal.reduce((total, format) => {
      if (format === 'export_certificate') {
        return total + this.invoices.length; // one certificate per invoice
      }
      return total + this.getOutputModes(format).length;
    }, 0);
  }

  /** Build the API payload for a format (shared by PDF and Excel calls). */
  private buildExportData(format: string): any {
    const invoiceIds = this.invoices.map(inv => inv._id).join(',');
    const data: any = {
      invoice_id: invoiceIds,
      type: format
    };
    if (format === 'standard_invoice' && this.editableData) {
      data.extra_data = this.editableData;
    }
    if (format === 'commercial_invoice' && this.commercialInvoiceData) {
      data.extra_data = this.commercialInvoiceData;
    }
    return data;
  }

  /**
   * Generate a single file (PDF or Excel) for a format and trigger its download.
   * onSuccess runs after the file downloads; onError runs on failure.
   */
  private generateFile(
    format: string,
    mode: 'pdf' | 'excel',
    onSuccess: () => void,
    onError: () => void
  ): void {
    const data = this.buildExportData(format);
    const invoiceNumbers = this.invoices.map(inv => inv.invoice_number).join(', ');
    const label = mode.toUpperCase();
    const request$ = mode === 'excel'
      ? this._generalService.generateTemplateExcel(data)
      : this._generalService.generatePDF(data);

    console.log(`Generating ${label} ${this.currentGeneratingIndex}/${this.totalToGenerate} - Invoices: ${invoiceNumbers}, Format: ${format}`);

    this.subscriptions.push(
      request$.subscribe({
        next: (res: any) => {
          if (res && res.data && res.data.url) {
            this.downloadFile(res.data.url, invoiceNumbers, format, mode === 'excel' ? 'xlsx' : 'pdf');
            onSuccess();
          } else {
            console.error('Invalid response format:', res);
            this.isGenerating = false;
            alert(`${label} generation failed for invoices ${invoiceNumbers} (${format}). No URL received.`);
            onError();
          }
        },
        error: (error: any) => {
          console.error(`Error generating ${label} for invoices ${invoiceNumbers} (${format}):`, error);
          this.isGenerating = false;
          alert(`Error generating ${label} for invoices ${invoiceNumbers} (${format}). Please try again.`);
          onError();
        }
      })
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  onFormatTabChange(formatValue: string): void {
    this.activeFormatTab = formatValue;
    // Reset active invoice index when switching format tabs
    this.activeInvoiceIndex = 0;
    console.log('Active format tab changed to:', this.activeFormatTab);
  }

  onInvoiceTabChange(index: number): void {
    this.activeInvoiceIndex = index;
    console.log('Active invoice index changed to:', this.activeInvoiceIndex);
  }

  onFormatToggle(formatValue: string): void {
    const index = this.selectedFormatsInModal.indexOf(formatValue);
    if (index > -1) {
      this.selectedFormatsInModal.splice(index, 1);
      // If we removed the active tab, switch to another tab
      if (this.activeFormatTab === formatValue && this.selectedFormatsInModal.length > 0) {
        this.activeFormatTab = this.selectedFormatsInModal[0];
      }
    } else {
      this.selectedFormatsInModal.push(formatValue);
      // If this is the first format, make it active
      if (this.selectedFormatsInModal.length === 1) {
        this.activeFormatTab = formatValue;
      }
    }
    console.log('Selected formats in modal:', this.selectedFormatsInModal);
  }

  isFormatSelected(formatValue: string): boolean {
    return this.selectedFormatsInModal.includes(formatValue);
  }

  toggleFormatDropdown(): void {
    this.showFormatDropdown = !this.showFormatDropdown;
  }

  closeFormatDropdown(): void {
    this.showFormatDropdown = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.showFormatDropdown && this.modalFormatDropdownContainer) {
      const clickedInside = this.modalFormatDropdownContainer.nativeElement.contains(event.target);
      if (!clickedInside) {
        this.closeFormatDropdown();
      }
    }
  }

  onViewModeChange(): void {
    console.log('View mode changed to:', this.selectedViewMode);
    this.updateProcessedInvoices();
  }

  // Update processed invoices when view mode or invoices change
  private updateProcessedInvoices(): void {
    if (this.selectedViewMode === 'merged' && this.invoices.length > 1) {
      this.processedInvoices = this.createMergedInvoice();
    } else {
      this.processedInvoices = this.invoices;
    }
  }

  // Get processed invoices based on view mode (cached)
  getProcessedInvoices(): Invoice[] {
    return this.processedInvoices;
  }

  // Create a merged invoice from multiple invoices
  private createMergedInvoice(): Invoice[] {
    if (this.invoices.length === 0) return [];

    const firstInvoice = this.invoices[0];
    const allVehicles: any[] = [];
    let totalAmount = 0;
    const invoiceNumbers: string[] = [];

    // Collect all vehicles and calculate total amount
    this.invoices.forEach(invoice => {
      allVehicles.push({
        ...invoice.vehicle,
        invoice_amount: invoice.invoice_amount,
        invoice_number: invoice.invoice_number,
        item: invoice.item
      });
      totalAmount += invoice.invoice_amount || 0;
      invoiceNumbers.push(invoice.invoice_number);
    });

    // Create merged invoice using first invoice as template
    const mergedInvoice: Invoice = {
      ...firstInvoice,
      invoice_number: invoiceNumbers.join(', '),
      invoice_amount: totalAmount,
      vehicles: allVehicles, // Add all vehicles as a new property
      merged: true // Flag to identify merged invoice
    };

    return [mergedInvoice];
  }

  onEditableDataChange(editableData: any): void {
    this.editableData = editableData;
    console.log('Editable data updated:', this.editableData);
  }

  onCommercialInvoiceDataChange(data: any): void {
    this.commercialInvoiceData = data;
  }

  generatePDF(): void {
    if (this.invoices.length === 0 || this.selectedFormatsInModal.length === 0) {
      return;
    }

    this.isGenerating = true;
    this.currentGeneratingIndex = 0;
    // Total = number of files across all formats (a "both" format counts as 2)
    this.totalToGenerate = this.computeTotalFiles();

    console.log(`Starting generation for ${this.invoices.length} invoice(s) × ${this.selectedFormatsInModal.length} format(s) → ${this.totalToGenerate} file(s)`);

    // Generate each format with all invoices
    this.generateNextPDF(0);
  }

  generateActivePDF(): void {
    if (this.invoices.length === 0 || !this.activeFormatTab) {
      return;
    }

    // Special handling for export certificate format - generate separately for each invoice
    if (this.activeFormatTab === 'export_certificate') {
      this.generateExportCertificatesForActiveTab();
      return;
    }

    // Resolve the output file(s) for this format (PDF, Excel, or both)
    const modes = this.getOutputModes(this.activeFormatTab);

    this.isGenerating = true;
    this.currentGeneratingIndex = 0;
    this.totalToGenerate = modes.length;

    console.log(`Starting generation for active format: ${this.activeFormatTab} (modes: ${modes.join(', ')})`);

    this.generateActiveModes(this.activeFormatTab, modes, 0);
  }

  /**
   * Generate the resolved output modes for the active format sequentially.
   * Used for the "Generate Active Tab" action.
   */
  private generateActiveModes(format: string, modes: ('pdf' | 'excel')[], modeIndex: number): void {
    // All modes for this format generated
    if (modeIndex >= modes.length) {
      this.isGenerating = false;
      this.currentGeneratingIndex = 0;
      this.totalToGenerate = 0;
      alert(`Successfully generated and downloaded ${modes.length} file(s) for ${this.invoices.length} invoice(s) in ${this.getFormatLabel(format)} format`);
      return;
    }

    this.currentGeneratingIndex = modeIndex + 1;

    this.generateFile(
      format,
      modes[modeIndex],
      () => setTimeout(() => this.generateActiveModes(format, modes, modeIndex + 1), 500),
      () => { /* error already surfaced + generation stopped */ }
    );
  }

  /**
   * Generate export certificates for active tab
   * Export certificates are generated separately for each invoice/vehicle
   */
  private generateExportCertificatesForActiveTab(): void {
    this.isGenerating = true;
    this.currentGeneratingIndex = 0;
    this.totalToGenerate = this.invoices.length; // One PDF per invoice

    console.log(`Starting export certificate generation for ${this.invoices.length} invoice(s)`);

    // Start generating from first invoice
    this.generateNextExportCertificate(0);
  }

  /**
   * Generate export certificate for a specific invoice
   */
  private generateNextExportCertificate(invoiceIndex: number): void {
    // Check if we've processed all invoices
    if (invoiceIndex >= this.invoices.length) {
      // All export certificates generated
      this.isGenerating = false;
      this.currentGeneratingIndex = 0;
      this.totalToGenerate = 0;
      alert(`Successfully generated and downloaded ${this.invoices.length} export certificate(s)`);
      return;
    }

    // Update progress
    this.currentGeneratingIndex = invoiceIndex + 1;

    const invoice = this.invoices[invoiceIndex];
    const invoiceNumber = invoice.invoice_number;
    const vehicleId = invoice.vehicle?._id;

    if (!vehicleId) {
      console.error(`No vehicle ID found for invoice ${invoiceNumber}`);
      alert(`Error: No vehicle found for invoice ${invoiceNumber}`);
      this.isGenerating = false;
      return;
    }

    // Prepare data for export certificate API call
    const data: any = {
      type: 'vehicle-certificate-invoice',
      vehicle_id: vehicleId
    };

    console.log(`Generating export certificate ${this.currentGeneratingIndex}/${this.totalToGenerate} - Invoice: ${invoiceNumber}, Vehicle ID: ${vehicleId}`);

    // Generate PDF for this export certificate
    this.subscriptions.push(
      this._generalService.generatePDF(data).subscribe({
        next: (res: any) => {
          console.log(`Export certificate generation response for invoice ${invoiceNumber}:`, res);
          if (res && res.data && res.data.url) {
            // Download the PDF
            this.downloadPDF(res.data.url, invoiceNumber, 'export_certificate');

            // Generate next export certificate after a short delay
            setTimeout(() => {
              this.generateNextExportCertificate(invoiceIndex + 1);
            }, 500);
          } else {
            console.error('Invalid response format:', res);
            this.isGenerating = false;
            alert(`Export certificate generation failed for invoice ${invoiceNumber}. No URL received.`);
          }
        },
        error: (error: any) => {
          console.error(`Error generating export certificate for invoice ${invoiceNumber}:`, error);
          this.isGenerating = false;
          alert(`Error generating export certificate for invoice ${invoiceNumber}. Please try again.`);
        }
      })
    );
  }

  /**
   * Generate export certificates in batch mode (when generating all formats)
   * After all certificates are generated, continue with next format
   */
  private generateExportCertificatesInBatch(currentFormatIndex: number): void {
    console.log(`Generating export certificates in batch for ${this.invoices.length} invoice(s)`);
    this.generateExportCertificateInBatch(0, currentFormatIndex);
  }

  /**
   * Generate export certificate for a specific invoice in batch mode
   */
  private generateExportCertificateInBatch(invoiceIndex: number, formatIndex: number): void {
    // Check if we've processed all invoices for this format
    if (invoiceIndex >= this.invoices.length) {
      // All export certificates for this format generated, move to next format
      console.log(`Completed export certificates for all invoices, moving to next format`);
      setTimeout(() => {
        this.generateNextPDF(formatIndex + 1);
      }, 500);
      return;
    }

    const invoice = this.invoices[invoiceIndex];
    const invoiceNumber = invoice.invoice_number;
    const vehicleId = invoice.vehicle?._id;

    if (!vehicleId) {
      console.error(`No vehicle ID found for invoice ${invoiceNumber}, skipping...`);
      // Skip to next invoice
      setTimeout(() => {
        this.generateExportCertificateInBatch(invoiceIndex + 1, formatIndex);
      }, 100);
      return;
    }

    // Prepare data for export certificate API call
    const data: any = {
      type: 'vehicle-certificate-invoice',
      vehicle_id: vehicleId
    };

    this.currentGeneratingIndex++;
    console.log(`Generating export certificate ${this.currentGeneratingIndex}/${this.totalToGenerate} for invoice ${invoiceNumber} (${invoiceIndex + 1}/${this.invoices.length})`);

    // Generate PDF for this export certificate
    this.subscriptions.push(
      this._generalService.generatePDF(data).subscribe({
        next: (res: any) => {
          console.log(`Export certificate generation response for invoice ${invoiceNumber}:`, res);
          if (res && res.data && res.data.url) {
            // Download the PDF
            this.downloadPDF(res.data.url, invoiceNumber, 'export_certificate');

            // Generate next export certificate after a short delay
            setTimeout(() => {
              this.generateExportCertificateInBatch(invoiceIndex + 1, formatIndex);
            }, 500);
          } else {
            console.error('Invalid response format:', res);
            // Continue with next invoice even if this one failed
            setTimeout(() => {
              this.generateExportCertificateInBatch(invoiceIndex + 1, formatIndex);
            }, 500);
          }
        },
        error: (error: any) => {
          console.error(`Error generating export certificate for invoice ${invoiceNumber}:`, error);
          // Continue with next invoice even if this one failed
          setTimeout(() => {
            this.generateExportCertificateInBatch(invoiceIndex + 1, formatIndex);
          }, 500);
        }
      })
    );
  }

  private generateNextPDF(formatIndex: number): void {
    // Check if we've processed all formats
    if (formatIndex >= this.selectedFormatsInModal.length) {
      // All files generated
      const totalGenerated = this.totalToGenerate;
      this.isGenerating = false;
      this.currentGeneratingIndex = 0;
      this.totalToGenerate = 0;
      alert(`Successfully generated and downloaded ${totalGenerated} file(s)`);
      return;
    }

    const format = this.selectedFormatsInModal[formatIndex];

    // Special handling for export certificate format - generate separately for each invoice
    if (format === 'export_certificate') {
      this.generateExportCertificatesInBatch(formatIndex);
      return;
    }

    // Generate every output mode for this format, then move to the next format
    this.generateFormatModes(format, this.getOutputModes(format), 0, formatIndex);
  }

  /**
   * Generate all output modes (PDF/Excel) for one format in the batch flow,
   * then continue with the next format.
   */
  private generateFormatModes(
    format: string,
    modes: ('pdf' | 'excel')[],
    modeIndex: number,
    formatIndex: number
  ): void {
    // All modes for this format done, move to next format
    if (modeIndex >= modes.length) {
      setTimeout(() => this.generateNextPDF(formatIndex + 1), 500);
      return;
    }

    this.currentGeneratingIndex++;

    this.generateFile(
      format,
      modes[modeIndex],
      () => setTimeout(() => this.generateFormatModes(format, modes, modeIndex + 1, formatIndex), 500),
      () => { /* error already surfaced + generation stopped */ }
    );
  }

  private downloadPDF(url: string, invoiceNumber: string, format: string): void {
    this.downloadFile(url, invoiceNumber, format, 'pdf');
  }

  private downloadFile(url: string, invoiceNumber: string, format: string, extension: string): void {
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';

    // Get format label for better filename
    const formatLabel = this.getFormatLabel(format);

    // Create filename with invoice number and format
    const filename = `Invoice_${invoiceNumber}_${formatLabel.replace(/\s+/g, '_')}.${extension}`;
    link.download = filename;

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log(`Downloaded ${extension.toUpperCase()} for invoice ${invoiceNumber} (${formatLabel})`);
  }

  closeModal(): void {
    const modalElement = document.getElementById('kt_modal_view_invoice');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  getFormatLabel(formatValue: string): string {
    const format = this.pdfFormats.find(f => f.value === formatValue);
    return format ? format.label : formatValue;
  }

  openPdf(): void {
    if (this.pdfUrl) {
      window.open(this.pdfUrl, '_blank');
    }
  }
}
